<script setup>
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnalytics } from '@/shared/analytics'
import { downloadFile } from '@/shared/utils/file.js'
import { BRADLEY_ANALYTICS_EVENTS } from '../analytics/bradley-analytics.constants.js'
import { bradleySourcesModalAnalyticsPayload } from '../analytics/bradley-analytics.telemetry.js'
import { downloadBradleyConversationFile } from '../services/bradley-ai.service.js'
import { BRADLEY_SOURCE_TYPE, buildBradleySourcesFromMessage } from '../utils/bradley-sources.js'

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  message: {
    type: Object,
    default: null,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  conversationMessages: {
    type: Array,
    default: () => [],
  },
  uploadedDocuments: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['close'])

const { t } = useI18n()
const { trackEvent } = useAnalytics()
const toast = useToast()
const downloadingFileId = ref(null)

const sources = computed(() => buildBradleySourcesFromMessage(
  props.message,
  props.conversationMessages,
  props.uploadedDocuments,
))

const resultsCount = computed(() => sources.value.length)

const badgeConfig = {
  [BRADLEY_SOURCE_TYPE.USER_UPLOAD]: {
    labelKey: 'newBradley.sourcesModal.badges.userProvided',
    containerClass: 'bg-slate-100 text-slate-600',
    leadingIcon: 'pi-user',
    trailingIcon: 'pi-download',
  },
  [BRADLEY_SOURCE_TYPE.DSS_SHAREPOINT]: {
    labelKey: 'newBradley.sourcesModal.badges.dssSharepoint',
    containerClass: 'bg-dss-info-50 text-dss-info-600',
    leadingIcon: 'pi-folder',
    trailingIcon: 'pi-external-link',
  },
  [BRADLEY_SOURCE_TYPE.GENIE]: {
    labelKey: 'newBradley.sourcesModal.badges.dss360Data',
    containerClass: 'bg-dss-info-50 text-dss-info-600',
    leadingIcon: 'pi-database',
    trailingIcon: null,
  },
}

function baseSourceAnalyticsPayload(source) {
  return {
    ...bradleySourcesModalAnalyticsPayload(props.message, props.conversationMessages, props.conversationId),
    source_type: source?.type ?? null,
    source_id: source?.id ?? null,
    has_url: Boolean(source?.url),
  }
}

function handleClose(closeMethod) {
  trackEvent(BRADLEY_ANALYTICS_EVENTS.SOURCES_MODAL_CLOSED, {
    ...bradleySourcesModalAnalyticsPayload(props.message, props.conversationMessages, props.conversationId),
    close_method: closeMethod,
  })
  emit('close')
}

function formatSimilarity(similarity) {
  if (typeof similarity !== 'number') {
    return null
  }

  return `${(similarity * 100).toFixed(1)}%`
}

function getBadgeLabel(source) {
  if (source.type === BRADLEY_SOURCE_TYPE.GENIE && source.title && source.title !== source.name) {
    return `${t('newBradley.sourcesModal.badges.dss360Data')} • ${source.title}`
  }

  const config = badgeConfig[source.type]
  return config ? t(config.labelKey) : source.label
}

function canOpenSource(source) {
  if (source?.url) {
    return true
  }

  return source?.type === BRADLEY_SOURCE_TYPE.USER_UPLOAD
    && Boolean(source?.fileId)
    && Boolean(props.conversationId)
}

function handleBadgeAction(source, event) {
  if (source.url) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.SOURCE_LINK_CLICKED, baseSourceAnalyticsPayload(source))
    window.open(source.url, '_blank', 'noopener,noreferrer')
    return
  }

  event.preventDefault()
  if (!canOpenSource(source) || downloadingFileId.value) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.SOURCE_BADGE_CLICKED, baseSourceAnalyticsPayload(source))
    return
  }

  downloadUploadedSource(source)
}

async function downloadUploadedSource(source) {
  downloadingFileId.value = source.fileId
  try {
    const { blob, fileName } = await downloadBradleyConversationFile(props.conversationId, source.fileId)
    downloadFile(blob, fileName || source.title || 'download')
    trackEvent(BRADLEY_ANALYTICS_EVENTS.SOURCE_LINK_CLICKED, {
      ...baseSourceAnalyticsPayload(source),
      has_url: false,
    })
  }
  catch (error) {
    console.error('Failed to download Bradley uploaded source', error)
    toast.add({
      severity: 'error',
      summary: t('newBradley.toasts.downloadProjectFileFailed'),
      life: 4000,
    })
  }
  finally {
    downloadingFileId.value = null
  }
}
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0"
      enter-to-class="transform opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        @click.self="handleClose('overlay')"
      >
        <div class="inset-0 z-[-1] bg-black opacity-40 w-full h-full absolute top-0 left-0" />
        <div class="flex w-full max-w-[590px] max-h-[560px] flex-col overflow-hidden rounded-2xl border border-dss-surface-200 bg-dss-surface-0 shadow-xl">
          <div class="flex items-start justify-between border-b border-dss-surface-200 px-6 py-5">
            <div>
              <h2 class="font-inter text-xl font-bold leading-none tracking-[0.005em]">
                {{ $t('newBradley.sourcesModal.title') }}
              </h2>
              <p
                v-if="resultsCount > 0"
                class="mt-2 text-sm text-slate-500"
              >
                {{ $t('newBradley.sourcesModal.showingResults', { count: resultsCount }) }}
              </p>
            </div>
            <button
              type="button"
              class="rounded-lg p-2 transition-colors hover:bg-gray-100"
              :aria-label="$t('newBradley.sourcesModal.close')"
              @click="handleClose('x')"
            >
              <i class="pi pi-times text-lg text-gray-600" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-4">
            <div
              v-if="resultsCount === 0"
              class="flex flex-col items-center justify-center py-8 text-gray-500"
            >
              <i class="pi pi-file-excel text-4xl mb-3" />
              <p>{{ $t('newBradley.sourcesModal.noSources') }}</p>
            </div>

            <div
              v-else
              class="flex flex-col gap-3"
            >
              <article
                v-for="source in sources"
                :key="source.id"
                class="flex items-start gap-3 rounded-xl border border-dss-surface-200 bg-dss-surface-0 px-4 py-3"
              >
                <span
                  v-if="source.markers?.length"
                  class="size-7 shrink-0 flex items-center justify-center rounded-full bg-dss-surface-200 font-inter text-xs font-semibold text-slate-700"
                >{{ source.markers.join(', ') }}</span>
                <span
                  v-else
                  class="size-7 shrink-0 rounded-full bg-dss-surface-200"
                />

                <div class="min-w-0 flex-1">
                  <div class="truncate font-hellix text-sm font-medium leading-[163%] tracking-normal text-slate-800">
                    {{ source.title }}
                  </div>
                  <div
                    v-if="source.kindLabel"
                    class="mt-0.5 font-inter text-xs leading-[163%] text-slate-500"
                  >
                    Tipo: {{ source.kindLabel }}
                  </div>
                  <div class="mt-1 flex items-center gap-3">
                    <div
                      v-if="formatSimilarity(source.similarity)"
                      class="min-w-0 flex-1 font-inter text-xs font-semibold leading-[163%] tracking-normal text-slate-500"
                    >
                      {{ $t('newBradley.sourcesModal.similarity') }}: {{ formatSimilarity(source.similarity) }}
                    </div>
                    <div
                      v-else
                      class="flex-1"
                    />
                    <component
                      :is="source.url ? 'a' : 'button'"
                      :href="source.url || undefined"
                      :type="source.url ? undefined : 'button'"
                      :disabled="!source.url && (!canOpenSource(source) || downloadingFileId === source.fileId)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex shrink-0 items-center gap-1.5 rounded-[5px] px-3 py-1.5 text-xs font-medium"
                      :class="[
                        badgeConfig[source.type]?.containerClass,
                        canOpenSource(source) ? 'cursor-pointer' : 'cursor-default',
                      ]"
                      @click="handleBadgeAction(source, $event)"
                    >
                      <i
                        v-if="badgeConfig[source.type]?.leadingIcon"
                        :class="`pi ${badgeConfig[source.type].leadingIcon} text-xs2`"
                      />
                      <span class="whitespace-nowrap">{{ getBadgeLabel(source) }}</span>
                      <i
                        v-if="badgeConfig[source.type]?.trailingIcon"
                        :class="`pi ${badgeConfig[source.type].trailingIcon} text-xs2`"
                      />
                    </component>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <div class="flex justify-end border-t border-dss-surface-200 px-6 py-4">
            <button
              type="button"
              class="cursor-pointer rounded-lg bg-dss-primary-500 px-4 py-2 font-hellix text-sm font-medium leading-none text-white transition-colors hover:bg-dss-primary-600"
              @click="handleClose('footer')"
            >
              {{ $t('newBradley.sourcesModal.close') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>
